import { useEffect, useMemo, useRef, useState } from "react";
import { NumberInput } from "./Calculator";
import {
  INVESTIGATIONS,
  JAIL_CALL_MARKET,
  JAIL_CALL_MARKET_SOURCES,
  JAIL_CALL_MODEL,
  PRICING,
  RFI,
} from "./data";
import { C, fmtDuration, fmtMoneyFull } from "./theme";
import {
  Callout,
  Eyebrow,
  Pullquote,
  Section,
  Source,
  Step,
  SubSection,
  Table,
  Td,
} from "./ui";

// ===================================================================
// 11. Jail call intelligence for investigations
//
// This section describes the Investigations module: bulk jail-call
// upload, per-call analysis, and a ranked leads queue. Platform facts
// trace to the codebase (see the INVESTIGATIONS block in data.ts); the
// time projection is our own model and is labeled as such, exactly like
// the Section 07 calculator.
// ===================================================================

export function InvestigationsSection() {
  return (
    <Section
      id="investigations"
      number="11"
      title="Jail call intelligence for investigations"
      subtitle={
        <>
          The RFI's report types include follow-up and homicide investigations (§ I.A).
          Those cases run on evidence review as much as on report writing, and the
          largest unreviewed evidence source in most of them is jail calls. A detective
          working a shooting can have hundreds of calls from three inmates sitting in a
          vendor export, each ten to fifteen minutes long. The calls that matter (a
          threat, a witness being talked out of testifying, property being moved) sit
          buried in hours of small talk nobody has time to hear.
        </>
      }
    >
      <Pullquote>
        Upload fifty calls. Know which six to listen to, and why, without spending a
        day in headphones.
      </Pullquote>

      <SubSection
        title="From vendor export to ranked queue"
        subtitle="The detective's part takes minutes. Everything after the upload runs on our side, whether or not the browser stays open."
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
          <Step n={1} title="Create the investigation">
            Name the case, add squad members, and attest that the recordings are not
            attorney-client privileged communications. The attestation is recorded to
            the audit trail.
          </Step>
          <Step n={2} title="Drag in the export">
            Upload the vendor export as-is, fifty calls at a time. Duplicates are
            rejected before any bytes move. Once the upload completes, the detective
            can close the laptop; processing continues server-side.
          </Step>
          <Step n={3} title="Every call is read in full" color={C.accent}>
            Each call is transcribed with speakers separated. Spanish is transcribed
            natively and translated with slang preserved and glossed. The model then
            reads the entire transcript, not a sample, and scores its relevance.
          </Step>
          <Step n={4} title="Come back to a ranked queue" color={C.good}>
            Calls ranked by relevance, flagged moments with exact quotes anchored to
            timestamps, a cross-call investigation brief, an entity roster, and a
            glossary of coded terms. Every quote has a play button.
          </Step>
        </div>

        <p className="text-sm text-slate-700 leading-relaxed max-w-3xl mt-5">
          The first ranked results arrive while the batch is still processing; a
          fifty-call batch typically settles within the hour. The same investigation
          also accepts PDFs, spreadsheets, and photos, so phone records and ledgers
          become searchable alongside the calls, and a detective can ask questions of
          the whole investigation in plain language with cited, playable answers.
        </p>

        <div className="mt-6">
          <LeadsQueueMock />
        </div>
      </SubSection>

      <SubSection
        title="What the model listens for"
        subtitle="Flagged moments are drawn from a fixed set of categories, not free text, so the queue is filterable and the output is consistent across calls and across cases."
      >
        <div className="flex flex-wrap gap-2">
          {INVESTIGATIONS.leadCategories.map((c) => (
            <span
              key={c}
              className="text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded-full px-3 py-1"
            >
              {c}
            </span>
          ))}
        </div>
        <p className="text-sm text-slate-700 leading-relaxed max-w-3xl mt-4">
          Three-way calls are the classic jail relay pattern, and the model checks the
          transcript for a third voice rather than guessing from context. Coded
          language is why every call is read in full: a keyword search for a threat
          finds nothing, but a phrase like "handle that," or a term that keeps turning
          up in the wrong contexts, surfaces as a glossary entry with its likely
          meaning and the line it came from.
        </p>
      </SubSection>

      <SubSection
        title="The investigation brief"
        subtitle={
          <>
            One document that answers "where does this investigation stand," written
            across every call rather than one at a time.
          </>
        }
      >
        <p className="text-sm text-slate-700 leading-relaxed max-w-3xl">
          Once a batch settles, a rollup pass reads every per-call analysis together
          and writes the brief: a summary of what the calls establish, the key people
          and how they connect, a sequence of events, the open questions, and the
          short list of calls to hear first, with reasons that span calls ("calls 14
          and 31 discuss the same delivery"). That cross-call sentence is the part no
          single-call score can produce, and it is the difference between a stack of
          transcripts and a starting point for the case.
        </p>
        <p className="text-sm text-slate-700 leading-relaxed max-w-3xl mt-3">
          The brief keeps its history. Each new upload revises it rather than
          replacing it: the current version opens with what changed since the last
          one, the detective's confirmations, dismissals, and notes are never
          overwritten, and every prior version is preserved and included in the
          discovery export, so "what did the analysis say in March" has an answer.
          The plain-language chat is grounded in the same brief, which is why its
          answers stay consistent with what the document says.
        </p>
        <div className="mt-4 print:hidden">
          <BriefPreviewButton />
        </div>
      </SubSection>

      <SubSection
        title="What a fifty-call upload is worth"
        subtitle={
          <>
            The defaults model the batch above: {JAIL_CALL_MODEL.callsPerBatch} calls
            at {JAIL_CALL_MODEL.avgCallMin} minutes each. As in Section 07, every
            number is editable and the assumptions are ours, stated below the results.
          </>
        }
      >
        <JailCallProjection />
      </SubSection>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <Callout tone="warning" title="Ninety minutes against zero">
          <p>
            For many batches the comparison is not eight hours against ninety minutes.
            It is ninety minutes against zero: at a full day of listening per export,
            most calls are never reviewed, and the threat, the tampering, or the
            admission goes unheard. The module's first job is to make review happen at
            all; the saved hours are what make that affordable.
          </p>
        </Callout>
        <Callout tone="legal" title="Built for discovery obligations">
          <p>
            The model flags passages for review; it never concludes. A lead reads
            "this call discusses a witness's court appearance, review it," not "the
            suspect tampered with a witness." Dismissing a lead is a status change,
            never a deletion, and the complete AI record, including dismissed leads,
            is exportable for discovery. Any call can be marked privileged after
            upload: it is excluded from analysis and search while the file is retained
            for chain of custody.
          </p>
        </Callout>
      </div>

      <SubSection
        title="What agencies pay for this today"
        subtitle="Jail-call intelligence is an established market. The dominant model is facility-wide monitoring sold as a service, priced at the facility or the system."
      >
        <Table minWidth={620} head={["Buyer", "Service", "Published price"]}>
          <tr>
            <Td strong className="whitespace-nowrap">
              ~{JAIL_CALL_MARKET.verusFacilityInmates.toLocaleString("en-US")}-inmate
              facility
            </Td>
            <Td>Verus monitoring service (LEO Technologies)</Td>
            <Td strong className="whitespace-nowrap">
              {fmtMoneyFull(JAIL_CALL_MARKET.verusFacilityLow)}–
              {fmtMoneyFull(JAIL_CALL_MARKET.verusFacilityHigh)} / yr
            </Td>
          </tr>
          <tr>
            <Td strong className="whitespace-nowrap">
              Florida Dept. of Corrections
            </Td>
            <Td>
              Up to {(JAIL_CALL_MARKET.floridaMinutes / 1_000_000).toFixed(0)} million
              monitored minutes over one year (5¢ per minute at full utilization)
            </Td>
            <Td strong className="whitespace-nowrap">
              {fmtMoneyFull(JAIL_CALL_MARKET.floridaContract)}
            </Td>
          </tr>
        </Table>

        <p className="text-sm text-slate-700 leading-relaxed max-w-3xl mt-4">
          Those programs answer a different question. They watch a whole facility
          continuously, on the vendor's keyword lists, and what reaches a detective is
          an alert from someone else's queue. This module is priced for the other end
          of the problem: a named detective, a specific case, and the calls already
          sitting in an export. At ${INVESTIGATIONS.seatPricePerMonth} per investigator
          per month, a ten-investigator unit runs $
          {(INVESTIGATIONS.seatPricePerMonth * 12 * 10).toLocaleString("en-US")} a
          year, a fraction of the facility-wide figures above and scoped to the cases
          the department actually works.
        </p>

        <Source>{JAIL_CALL_MARKET_SOURCES}. Per-minute and per-unit figures are arithmetic on those published amounts.</Source>
      </SubSection>

      <SubSection
        title="Licensing"
        subtitle="Licensed per investigator, separately from report writing."
      >
        <p className="text-sm text-slate-700 leading-relaxed max-w-3xl">
          Investigations is licensed at ${INVESTIGATIONS.seatPricePerMonth} per
          investigator per month, and a department purchase covers all of the
          department's investigators. Investigators keep every feature of the
          report-writing tier; their seats are simply upgraded, so the incremental
          cost is ${INVESTIGATIONS.seatPricePerMonth - PRICING.perOfficerPerYear / 12}{" "}
          per investigator per month over the report-writing license. Report-writing
          pricing for all other officers is unchanged from Section 14.
        </p>
        <p className="text-sm text-slate-700 leading-relaxed max-w-3xl">
          As with report writing, we would welcome an evaluation on a
          closed case, where {RFI.department} already knows what the calls contain
          and can grade the ranking against ground truth.
        </p>
      </SubSection>

      <Source>
        Lead categories, language handling, privilege controls, and the discovery
        export are shipped platform functionality. The review-time projection is our
        model, with assumptions as stated; it is not a stopwatch study.
      </Source>
    </Section>
  );
}

// ===================================================================
// Brief preview popover
//
// A miniature of the product's actual Brief tab (see
// client/src/components/Investigations/tabs/BriefTab.tsx): masthead with
// eyebrow + headline, numbered sections, red-badged "top calls" cards,
// divided people rows, the blue-dot timeline, and the open-questions
// card. Same layout at reading-glass scale; the data is illustrative
// and consistent with the leads mock above.
// ===================================================================

function BriefPreviewButton() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Click anywhere outside (or press Escape) to dismiss.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-3 py-1.5 hover:bg-blue-100 transition-colors"
      >
        <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path d="M10 4C5.5 4 2.1 7.6 1 10c1.1 2.4 4.5 6 9 6s7.9-3.6 9-6c-1.1-2.4-4.5-6-9-6zm0 9.5A3.5 3.5 0 1110 6.5a3.5 3.5 0 010 7zm0-5.5a2 2 0 100 4 2 2 0 000-4z" />
        </svg>
        Preview a brief
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-3 z-40 w-[min(30rem,88vw)]">
          <div className="rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
            <BriefMini />
            <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-[10px] text-slate-500">
              Rendered as it appears in the product, reduced. Illustrative data,
              consistent with the flagged moments above.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BriefMini() {
  return (
    <div className="p-4 space-y-4 max-h-[26rem] overflow-y-auto">
      {/* Masthead */}
      <header>
        <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-blue-600">
          Brief · Case 26-041877
        </p>
        <h5 className="mt-1.5 text-[15px] leading-snug font-bold tracking-tight text-slate-900">
          Calls center on aligning statements ahead of testimony and moving stored
          property before Friday
        </h5>
        <p className="mt-1 text-[9px] text-slate-400">Generated August 12 · version 3</p>
        <p className="mt-1.5 text-[10px] leading-4 text-slate-600 bg-blue-50 border border-blue-100 rounded-md px-2 py-1">
          <span className="font-semibold text-blue-700">What changed:</span> 6 new
          calls analyzed; the glossary adds "la troca"; the Friday deadline now
          appears in three calls.
        </p>
      </header>

      {/* 01 Summary */}
      <section>
        <MiniSectionTitle n="01" title="Call summary" />
        <p className="mt-1.5 text-[10.5px] leading-4 text-slate-700">
          Across 41 analyzed calls, the recurring threads are coordination of a
          shared account of the incident, movement of property referred to as "la
          troca," and repeated contact with a third party reached by relay. Tone
          shifts sharply after the August 2 bail hearing.
        </p>
      </section>

      {/* 02 Top calls */}
      <section>
        <MiniSectionTitle n="02" title="Top calls to review" />
        <div className="mt-2.5 grid grid-cols-2 gap-2">
          {[
            "Call 14: both parties rehearse a 'hacking problem' account that matches no earlier statement.",
            "Calls 27 and 31 discuss the same Friday deadline; 31 adds a third voice mid-call.",
          ].map((reason, i) => (
            <div
              key={i}
              className="relative rounded-lg border border-slate-200 px-2.5 pt-3 pb-2"
            >
              <span className="absolute -top-2 left-2.5 w-4 h-4 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center">
                {i + 1}
              </span>
              <p className="text-[10px] leading-4 text-slate-700">{reason}</p>
              <span className="mt-1 inline-flex items-center gap-1 text-[9px] font-medium text-red-700">
                <svg width="8" height="8" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path d="M6 4.5v11l9-5.5-9-5.5z" />
                </svg>
                Open call
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* 03 Key individuals */}
      <section>
        <MiniSectionTitle n="03" title="Key individuals" />
        <ul className="mt-1.5 divide-y divide-slate-100 border-y border-slate-100">
          {[
            {
              name: '"Primo"',
              role: "Recipient of instructions",
              note: 'Asked twice to move "la troca" before Friday.',
            },
            {
              name: "Unidentified third voice",
              role: "Joined Call 31",
              note: "Connected mid-call by the primary contact; not on the approved list.",
            },
          ].map((p) => (
            <li key={p.name} className="py-1.5 grid grid-cols-[7rem_1fr] gap-x-3">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold text-slate-900 leading-tight">
                  {p.name}
                </p>
                <p className="text-[8.5px] text-slate-500">{p.role}</p>
              </div>
              <p className="text-[10px] leading-4 text-slate-700">{p.note}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* 04 Sequence of events */}
      <section>
        <MiniSectionTitle n="04" title="Sequence of events" />
        <ol className="mt-2 relative border-l-2 border-slate-200 ml-1 space-y-2">
          {[
            { date: "Aug 02", text: "Bail hearing; call tone shifts the same evening." },
            { date: "Aug 05", text: '"Move it before Friday" first appears (Call 27).' },
          ].map((t) => (
            <li key={t.date} className="pl-3.5 relative">
              <span className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-white border-2 border-blue-500" />
              <p className="text-[8.5px] font-semibold uppercase tracking-wider text-slate-400 tabular-nums">
                {t.date}
              </p>
              <p className="text-[10px] leading-4 text-slate-700">{t.text}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* 05 Open questions */}
      <section>
        <MiniSectionTitle n="05" title="Open questions" />
        <ol className="mt-2 rounded-lg border border-slate-200 bg-slate-50 divide-y divide-slate-200">
          {['What is "la troca" and where is it stored?', "Who is the third voice on Call 31?"].map(
            (q, i) => (
              <li key={q} className="flex gap-2 px-2.5 py-1.5">
                <span className="text-[9px] font-semibold tabular-nums text-slate-400 shrink-0">
                  0{i + 1}
                </span>
                <p className="text-[10px] leading-4 text-slate-700">{q}</p>
              </li>
            )
          )}
        </ol>
      </section>

      <footer className="border-t border-slate-100 pt-2">
        <p className="text-[8.5px] leading-3 text-slate-400">
          Generated from the call transcripts and uploaded files. Verify against the
          audio before relying on any statement.
        </p>
      </footer>
    </div>
  );
}

function MiniSectionTitle({ n, title }: { n: string; title: string }) {
  return (
    <div className="flex items-baseline gap-2 border-b border-slate-200 pb-1">
      <span className="text-[9px] font-semibold tabular-nums text-blue-600">{n}</span>
      <h6 className="text-[11px] font-semibold tracking-tight text-slate-900">{title}</h6>
    </div>
  );
}

// ===================================================================
// Leads queue mock
// ===================================================================

const MOCK_LEADS: {
  severity: "High" | "Medium";
  category: string;
  meta: string;
  quote: string;
  translation?: string;
  explanation: string;
  playFrom: string;
}[] = [
  {
    severity: "High",
    category: "Witness tampering",
    meta: "Call 14 · 06:12 · Inmate → co-defendant",
    quote:
      "You and I need to be on the same page to block what they said. We just say there was a hacking problem I had a hard time understanding, and leave it at that.",
    explanation:
      "Two callers settle on a single account in response to statements a third party has already given. Review who 'they' refers to and which statement is being countered.",
    playFrom: "06:12",
  },
  {
    severity: "Medium",
    category: "Coded language",
    meta: "Call 27 · 04:32 · Inmate → cousin (Spanish)",
    quote: "Dile al primo que mueva la troca antes del viernes.",
    translation: "Tell the cousin to move the truck before Friday.",
    explanation:
      '"La troca" appears in three calls in contexts inconsistent with a vehicle. The glossary proposes it refers to stored property.',
    playFrom: "04:32",
  },
  {
    severity: "Medium",
    category: "Three-way call",
    meta: "Call 31 · 00:58 · third voice joins",
    quote: "Hold on, I got him on the other line right now.",
    explanation:
      "A third participant is connected mid-call. Relay calls of this kind violate facility policy and are how instructions reach people an inmate cannot call directly.",
    playFrom: "00:58",
  },
];

function LeadsQueueMock() {
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-5 pt-4 pb-3 flex items-baseline justify-between gap-3 border-b border-slate-100">
        <Eyebrow>The leads queue</Eyebrow>
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
          Ranked across all calls
        </span>
      </div>
      <div className="divide-y divide-slate-100">
        {MOCK_LEADS.map((l) => (
          <div key={l.meta} className="px-5 py-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: l.severity === "High" ? C.danger : C.amber }}
                />
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  {l.severity}
                </span>
              </span>
              <span className="text-[10px] uppercase tracking-wider font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
                {l.category}
              </span>
              <span className="text-xs font-mono text-slate-500">{l.meta}</span>
            </div>
            <p className="text-sm text-slate-900 leading-relaxed mt-2">
              "{l.quote}"
              {l.translation && (
                <span className="text-slate-500 italic"> · "{l.translation}"</span>
              )}
            </p>
            <p className="text-xs text-slate-600 leading-relaxed mt-1 max-w-2xl">
              {l.explanation}
            </p>
            <div className="flex items-center gap-4 mt-2.5">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700">
                <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path d="M6 4.5v11l9-5.5-9-5.5z" />
                </svg>
                Play from {l.playFrom}
              </span>
              <span className="text-xs text-slate-400">Confirm · Dismiss · Add note</span>
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-200 px-5 py-2.5 bg-slate-50 text-[11px] text-slate-500">
        Illustrative mock; the examples are representative and anonymized. On the
        real queue, quotes are verbatim from the recording and every lead's play
        button seeks the audio to that moment, so the quote is checkable in one click.
      </div>
    </div>
  );
}

// ===================================================================
// Projection: what a batch costs to review, with and without
// ===================================================================

type ProjectionInputs = {
  calls: number;
  avgMin: number;
  verifySharePct: number;
};

const PROJECTION_DEFAULTS: ProjectionInputs = {
  calls: JAIL_CALL_MODEL.callsPerBatch,
  avgMin: JAIL_CALL_MODEL.avgCallMin,
  verifySharePct: JAIL_CALL_MODEL.verifySharePct,
};

function computeProjection(i: ProjectionInputs) {
  const audioMin = i.calls * i.avgMin;
  const manualMin = audioMin / JAIL_CALL_MODEL.reviewSpeed;
  const platformMin =
    JAIL_CALL_MODEL.briefReviewMin + audioMin * (i.verifySharePct / 100);
  const savedMin = Math.max(0, manualMin - platformMin);
  // One export per week; four is deliberately the low, round count.
  const monthlyMin = savedMin * 4;
  return { audioMin, manualMin, platformMin, savedMin, monthlyMin };
}

function JailCallProjection() {
  const [inputs, setInputs] = useState<ProjectionInputs>(PROJECTION_DEFAULTS);
  const r = useMemo(() => computeProjection(inputs), [inputs]);
  const set = <K extends keyof ProjectionInputs>(k: K) => (v: number) =>
    setInputs((prev) => ({ ...prev, [k]: v }));
  const isDefault = useMemo(
    () =>
      (Object.keys(PROJECTION_DEFAULTS) as (keyof ProjectionInputs)[]).every(
        (k) => inputs[k] === PROJECTION_DEFAULTS[k]
      ),
    [inputs]
  );

  const max = Math.max(r.manualMin, 1);

  return (
    <div className="grid lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] gap-6 items-start">
      {/* ---------------- Inputs ---------------- */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
        <div className="flex items-baseline justify-between mb-4">
          <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">
            The batch
          </h4>
          {!isDefault && (
            <button
              type="button"
              onClick={() => setInputs(PROJECTION_DEFAULTS)}
              className="text-[11px] text-blue-700 hover:text-blue-900 underline underline-offset-2 print:hidden"
            >
              Reset
            </button>
          )}
        </div>
        <div className="space-y-4">
          <NumberInput
            label="Calls in the export"
            value={inputs.calls}
            onChange={set("calls")}
            min={0}
            format="comma"
            hint="A week of calls for a few inmates of interest is commonly this size or larger."
          />
          <NumberInput
            label="Average minutes per call"
            value={inputs.avgMin}
            onChange={set("avgMin")}
            min={0}
            format="comma"
            hint="Facility phone systems typically cap calls at 15 to 30 minutes."
          />
          <NumberInput
            label="Audio the detective still verifies by ear"
            value={inputs.verifySharePct}
            onChange={set("verifySharePct")}
            min={0}
            max={100}
            format="percent"
            hint="Share of the batch played back to check flagged moments and the top-ranked calls. Raise it and the saving shrinks; the flagged quotes deserve their play button either way."
          />
        </div>
      </div>

      {/* ---------------- Results ---------------- */}
      <div className="space-y-5 min-w-0">
        <div className="grid sm:grid-cols-3 gap-3">
          <ProjectionTile label="Audio in the batch" value={fmtDuration(r.audioMin)} />
          <ProjectionTile label="Reviewed by ear" value={fmtDuration(r.manualMin)} />
          <ProjectionTile
            label="With the platform"
            value={fmtDuration(r.platformMin)}
            accent
          />
        </div>

        <div className="rounded-lg border border-slate-200 p-5">
          <h4 className="text-sm font-semibold text-slate-900 mb-4">
            One batch, before and after
          </h4>
          <div className="space-y-3">
            {[
              {
                label: "Listening through the export",
                min: r.manualMin,
                color: C.manual,
              },
              {
                label: "Brief, leads queue, and spot checks",
                min: r.platformMin,
                color: C.primary,
              },
            ].map((row) => (
              <div key={row.label}>
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-xs font-medium text-slate-700">{row.label}</span>
                  <span className="text-sm font-bold tabular-nums text-slate-900">
                    {fmtDuration(row.min)}
                  </span>
                </div>
                <div className="h-7 bg-slate-100 rounded-md overflow-hidden">
                  <div
                    className="h-full rounded-md transition-all duration-700 ease-out"
                    style={{
                      width: `${Math.max((row.min / max) * 100, 1.5)}%`,
                      backgroundColor: row.color,
                    }}
                  />
                </div>
              </div>
            ))}
            <div className="flex items-center gap-3 pt-2 mt-1 border-t border-slate-100">
              <span
                className="text-[11px] uppercase tracking-wider font-bold"
                style={{ color: C.good }}
              >
                Saved
              </span>
              <div className="flex-1 h-2 bg-emerald-50 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700 ease-out"
                  style={{
                    width: `${(r.savedMin / max) * 100}%`,
                    backgroundColor: C.good,
                  }}
                />
              </div>
              <span className="text-sm font-bold tabular-nums" style={{ color: C.good }}>
                {fmtDuration(r.savedMin)}
              </span>
            </div>
          </div>
          <p className="text-xs text-slate-600 mt-4">
            At one export per week, that is roughly{" "}
            <span className="font-bold text-slate-900">{fmtDuration(r.monthlyMin)}</span>{" "}
            a month returned to a single detective's caseload.
          </p>
        </div>

        <p className="text-[11px] text-slate-500 leading-relaxed">
          Assumptions: manual review is audio duration at{" "}
          {JAIL_CALL_MODEL.reviewSpeed}× playback, the same re-listen constant as the
          Section 07 model, which is deliberately generous to the manual side, since
          first-pass review of unfamiliar phone-quality audio with note-taking
          typically runs at or below real time. Platform time is{" "}
          {JAIL_CALL_MODEL.briefReviewMin} minutes to read the brief and leads queue
          plus the verification share at left. Excluded from both sides: logging notes,
          moving timestamps into reports, and the batches that today are never
          reviewed at all.
        </p>
      </div>
    </div>
  );
}

function ProjectionTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-lg p-4 border ${
        accent
          ? "bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200"
          : "bg-white border-slate-200"
      }`}
    >
      <div
        className={`text-[11px] uppercase tracking-wider font-semibold ${
          accent ? "text-emerald-700" : "text-slate-500"
        }`}
      >
        {label}
      </div>
      <div
        className={`mt-1.5 text-2xl font-bold tabular-nums leading-tight ${
          accent ? "text-emerald-700" : "text-slate-900"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
